import React from "react";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import {
  FiUsers,
  FiCalendar,
  FiBriefcase,
  FiHash,
  FiTag,
  FiLayers,
  FiMapPin,
  FiGrid,
  FiDollarSign,
  FiHome,
} from "react-icons/fi";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { formatToReal } from "../../../utils/utils";

const formatDate = (value) => {
  if (!value) return "—";
  if (typeof value !== "string") return String(value);
  const trimmed = value.trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("pt-BR");
};

const parseDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const str = String(value).trim();
  if (!str) return null;
  const brMatch = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (brMatch) {
    const [, dd, mm, yyyy] = brMatch;
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  }
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const formatRelativeTime = (value) => {
  const date = parseDateValue(value);
  if (!date) return "";
  const now = new Date();
  const diffMs = now - date;
  const isFuture = diffMs < 0;
  const absMs = Math.abs(diffMs);
  const days = Math.floor(absMs / (1000 * 60 * 60 * 24));

  if (days === 0) return "hoje";

  if (days < 30) {
    return `${isFuture ? "em" : "há"} ${days} dia${days === 1 ? "" : "s"}`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${isFuture ? "em" : "há"} ${months} mês${months === 1 ? "" : "es"}`;
  }

  const years = Math.floor(months / 12);
  return `${isFuture ? "em" : "há"} ${years} ano${years === 1 ? "" : "s"}`;
};

const renderRelative = (value) => {
  const rel = formatRelativeTime(value);
  return rel ? ` • ${rel}` : "";
};

const formatFimRelacionamento = (value) => {
  if (!value) return "—";
  const str = String(value);
  if (str.startsWith("9999-12-31")) return "Vigente";
  return formatDate(str);
};

const TONE_STYLES = {
  blue: "bg-blue-50 text-blue-700 ring-blue-100",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  violet: "bg-violet-50 text-violet-700 ring-violet-100",
  amber: "bg-amber-50 text-amber-700 ring-amber-100",
  rose: "bg-rose-50 text-rose-700 ring-rose-100",
  slate: "bg-slate-100 text-slate-700 ring-slate-200",
};

const InfoCard = ({
  label,
  value,
  helperText,
  icon: Icon,
  tone = "blue",
  className = "",
}) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white p-3 ${className}`}
  >
    <div className="flex items-start justify-between gap-3">
      <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
        {label}
      </p>
      {Icon && (
        <span
          className={`inline-flex h-8 w-8 min-w-8 items-center justify-center rounded-full ring-1 ${TONE_STYLES[tone]}`}
        >
          <Icon className="h-4 w-4" />
        </span>
      )}
    </div>
    <p className="mt-1 text-base font-semibold text-slate-900">{value}</p>
    {helperText && <p className="text-xs text-slate-400">{helperText}</p>}
  </div>
);

const RelationshipCard = ({ item }) => {
  const docLabel = item?.numeroDocumentoEntidadeRelacionada
    ? `${item?.tipoDocumentoEntidadeRelacionada || "Doc"}: ${
        item.numeroDocumentoEntidadeRelacionada
      }`
    : "Documento nao informado";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400">
            Relacionamento
          </p>
          <p className="text-base font-semibold text-slate-900">
            {item?.nomeEntidadeRelacionada || "Nome nao informado"}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {item?.nomeRelacionamento || "—"} •{" "}
            {item?.tipoRelacionamento || "—"}
          </p>
        </div>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-600">
          {item?.nivelRelacionamento
            ? item?.nivelRelacionamento.toUpperCase() === "DIRECT"
              ? "Direto"
              : item?.nivelRelacionamento
            : "—"}
        </span>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-2 text-xs text-slate-600">
        <div className="rounded-xl bg-slate-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
            Documento
          </div>
          <div className="font-semibold">{docLabel}</div>
          {item?.paisDocumentoEntidadeRelacionada && (
            <div className="text-[11px] text-slate-500">
              {item.paisDocumentoEntidadeRelacionada}
            </div>
          )}
        </div>
        <div className="rounded-xl bg-slate-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
            Origem
          </div>
          <div className="font-semibold">
            {item?.origemDadosRelacionamento || "—"}
          </div>
        </div>
        <div className="rounded-xl bg-slate-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
            Início
          </div>
          <div className="font-semibold">
            {formatDate(item?.dataInicioRelacionamento)}
          </div>
        </div>
        <div className="rounded-xl bg-slate-50 px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
            Fim
          </div>
          <div className="font-semibold">
            {formatFimRelacionamento(item?.dataFimRelacionamento)}
          </div>
        </div>
      </div>
    </div>
  );
};

const Timeline = ({ items, renderItem }) => {
  if (!items || !items.length) return <NoOcurrence />;
  return (
    <div className="relative">
      <span className="absolute left-[10px] top-0 bottom-0 w-px bg-slate-200" />
      <div className="space-y-4">
        {items.map((it, idx) => (
          <div key={idx} className="relative pl-7">
            <span className="absolute left-[6px] top-2 h-2.5 w-2.5 rounded-full bg-blue-600" />
            <div>{renderItem(it)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const HistoricoEmpresaPJQuery = ({ data }) => {
  const quadroQsa = data?.quadroQsa || {};
  const historico = data?.historicoDadosBasicos || {};

  const relacionamentosAtuais = quadroQsa?.relacionamentosAtuais || [];
  const relacionamentosHistoricos = quadroQsa?.relacionamentosHistoricos || [];

  const historyTabs = [
    {
      key: "nomes",
      title: "Alterações nome",
      items: historico?.historicoNomes || [],
      renderItem: (item) => (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
            Alteração: {formatDate(item?.dataAlteracao)}
            {renderRelative(item?.dataAlteracao)}
          </p>
          <p className="text-sm font-semibold text-slate-900 mt-1">
            Razão Social: {item?.razaoSocial || item?.nome || "—"}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Nome fantasia: {item?.nomeFantasia || "—"}
          </p>
        </div>
      ),
    },
    {
      key: "nomeFantasia",
      title: "Nome fantasia",
      items: historico?.historicoNomeFantasia || [],
      renderItem: (item) => (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
            Alteração: {formatDate(item?.dataAlteracao)}
            {renderRelative(item?.dataAlteracao)}
          </p>
          <p className="text-sm font-semibold text-slate-900 mt-1">
            Nome fantasia: {item?.nomeFantasia || "—"}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Razão social: {item?.razaoSocial || item?.nome || "—"}
          </p>
        </div>
      ),
    },
    {
      key: "regime",
      title: "Regime tributário",
      items: historico?.historicoRegimeTributario || [],
      renderItem: (item) => (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 ">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
            Alteração: {formatDate(item?.dataAlteracao)}
            {renderRelative(item?.dataAlteracao)}
          </p>
          <p className="text-sm font-semibold text-slate-900 mt-1">
            Regime: {item?.regimeTributario || "—"}
          </p>
        </div>
      ),
    },
    {
      key: "situacao",
      title: "Situação cadastral",
      items: historico?.historicoSituacaoCadastral || [],
      renderItem: (item) => (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 ">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
            Alteração: {formatDate(item?.dataAlteracao)}
            {renderRelative(item?.dataAlteracao)}
          </p>
          <p className="text-sm font-semibold text-slate-900 mt-1">
            Situação cadastral: {item?.situacaoCadastral || "—"}
          </p>
        </div>
      ),
    },
    {
      key: "cnae",
      title: "CNAE",
      items: historico?.historicoCnae || [],
      renderItem: (item) => (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 ">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                Alteração: {formatDate(item?.dataAlteracao)}
                {renderRelative(item?.dataAlteracao)}
              </p>
              <p className="text-sm font-semibold text-slate-900 mt-1">
                Atividade: {item?.atividade || "—"}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Divisão: {item?.divisaoAtividadeEconomica || "—"}
              </p>
              <p className="text-xs text-slate-500">
                {item?.grupoAtividadeEconomica || "—"}
              </p>
            </div>
            {item?.ehAtividadeEconomicaPrincipal && (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-emerald-700">
                Principal
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Código: {item?.codigo || "—"}
          </p>
        </div>
      ),
    },
    {
      key: "capital",
      title: "Capital",
      items: historico?.historicoCapital || [],
      renderItem: (item) => (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
            Alteração: {formatDate(item?.dataAlteracao)}
            {renderRelative(item?.dataAlteracao)}
          </p>
          <p className="text-sm font-semibold text-slate-900 mt-1">
            Capital: {item?.capital ? formatToReal(item.capital) : "—"}
          </p>
        </div>
      ),
    },
  ];

  const [activeHistoryTab, setActiveHistoryTab] = React.useState("nomes");
  const currentHistoryTab =
    historyTabs.find((tab) => tab.key === activeHistoryTab) || historyTabs[0];

  return (
    <div className="space-y-6">
      <TitleStripe>
        <div className="flex items-center gap-2">
          <FiUsers className="text-blue-700" />
          <span>Histórico da Empresa</span>
        </div>
      </TitleStripe>

      <div className="mt-4 rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-white p-4">
        <div className="grid gap-3 grid-cols-4">
          <InfoCard
            label="Nome atual"
            value={historico?.nomeAtual || "—"}
            className=" col-span-2"
            icon={FiBriefcase}
            tone="blue"
          />
          <InfoCard
            label="Idade"
            value={historico?.idade ? `${historico.idade} anos` : "—"}
            icon={FiCalendar}
            tone="emerald"
          />
          <InfoCard
            label="Total de alterações"
            value={historico?.totalAlteracoes ?? "—"}
            icon={FiHash}
            tone="violet"
          />
          <InfoCard
            label="Última alteração"
            value={historico?.ultimaDataAlteracao || "—"}
            icon={FiLayers}
            tone="amber"
            helperText={formatRelativeTime(historico?.ultimaDataAlteracao)}
          />
          <InfoCard
            label="Última alteração de nome"
            value={historico?.ultimaAlteracaoNome || "—"}
            icon={FiTag}
            tone="rose"
            helperText={formatRelativeTime(historico?.ultimaAlteracaoNome)}
          />
          <InfoCard
            label="Última alteração de natureza jurídica"
            value={historico?.ultimaAlteracaoNaturezaJuridica || "—"}
            icon={FiMapPin}
            tone="slate"
            helperText={formatRelativeTime(
              historico?.ultimaAlteracaoNaturezaJuridica
            )}
          />
        </div>
      </div>

      <div>
        <TitleStripe>
          <div className="flex items-center gap-2">
            <FiBriefcase className="text-blue-700" />
            <span>Quadro QSA</span>
          </div>
        </TitleStripe>

        <div className="mt-4 grid gap-4">
          <div>
            <div className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-700">
              <HiOutlineBuildingOffice2 className="text-slate-500" />
              <span>
                Relacionamentos atuais ({relacionamentosAtuais.length})
              </span>
            </div>
            <div className="grid gap-3">
              {relacionamentosAtuais.length ? (
                relacionamentosAtuais.map((item, idx) => (
                  <RelationshipCard key={`qsa_atual_${idx}`} item={item} />
                ))
              ) : (
                <NoOcurrence />
              )}
            </div>
          </div>

          <div className="mt-5 mb-2">
            <div className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-700">
              <HiOutlineBuildingOffice2 className="text-slate-500" />
              <span>
                Relacionamentos passados ({relacionamentosHistoricos.length})
              </span>
            </div>
            <Timeline
              items={relacionamentosHistoricos}
              renderItem={(item) => <RelationshipCard item={item} />}
            />
          </div>
        </div>
      </div>

      <div>
        <TitleStripe>
          <div className="flex items-center gap-2">
            <FiCalendar className="text-blue-700" />
            <span>Histórico de dados básicos</span>
          </div>
        </TitleStripe>

        <div className="mt-6">
          <div className="flex flex-wrap gap-2">
            {historyTabs.map((tab) => {
              const active = tab.key === activeHistoryTab;
              const count = tab.items?.length || 0;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveHistoryTab(tab.key)}
                  className={[
                    "inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-xs font-semibold transition",
                    active
                      ? "bg-slate-700 text-white shadow-sm"
                      : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50",
                  ].join(" ")}
                >
                  <span>{`${tab.title} (${count})`}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-4">
            <Timeline
              items={currentHistoryTab?.items || []}
              renderItem={currentHistoryTab?.renderItem}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoricoEmpresaPJQuery;
